import React from "react";
import PropTypes from "prop-types";
import { CopyToClipboard } from "react-copy-to-clipboard";
//Material imports
import Typography from "@mui/material/Typography";
import makeStyles from "@mui/styles/makeStyles";
//Limitless imports
import { useTranslation } from "@limitless/common/hooks";
import { useExpertMessageResponseContext, useExpertProfileContext } from "../../../expert";
import RemoteContent from "../remote-content/RemoteContent";
import PortalButton from "../buttons/portal-button/PortalButton";
import { createPortalURL, getPortal } from "../../utils/PortalHelper";
const useStyles = makeStyles(theme => ({
  knowledgeArticleContainer: {
    maxWidth: 460
  },
  contentContainer: {
    padding: 16
  },
  footerContainer: {
    display: "flex",
    justifyContent: "space-around",
    paddingTop: 16,
    paddingBottom: 16,
    position: "sticky",
    bottom: 0,
    backgroundColor: theme.background.paper
  },
  copyAnswerButton: {
    alignSelf: "center"
  },
  copyAnswerButtonSuccess: {
    backgroundColor: "#4caf50",
    "&:hover": {
      backgroundColor: "#4caf50"
    }
  }
}));
const KnowledgeArticle = ({
  article,
  copyToClipboard = false
}) => {
  const [plainText, setPlainText] = React.useState("");
  const [previousResponse, setPreviousResponse] = React.useState("");
  const [copied, setCopied] = React.useState(false);
  const articleEl = React.useRef(null);
  const {
    messageResponse,
    updateMessageResponse
  } = useExpertMessageResponseContext();
  const {
    isManager
  } = useExpertProfileContext();
  const {
    t
  } = useTranslation();
  const classes = useStyles();
  React.useEffect(() => {
    loadCopyText();
  }, []);
  React.useEffect(() => {
    loadCopyText();
    setCopied(false);
  }, [article]);
  const loadCopyText = () => {
    if (document.getElementById("kb-article")) {
      setPlainText(document.getElementById("kb-article").innerText);
    }
  };
  const handleReplace = () => {
    setPreviousResponse(messageResponse);
    updateMessageResponse(plainText);
  };
  const handleAppend = () => {
    setPreviousResponse(messageResponse);
    //add a space if the last char isn't a space
    if (messageResponse.charAt(messageResponse.length - 1) === " ") {
      updateMessageResponse(`${messageResponse}${plainText}`);
    } else {
      updateMessageResponse(`${messageResponse} ${plainText}`);
    }
  };
  const handleUndo = () => {
    updateMessageResponse(previousResponse);
    setPreviousResponse("");
  };
  const handleEdit = () => {
    if (isManager) {
      window.open(createPortalURL(getPortal("knowledge-portal"), `edit-article/${article.label}`), "_blank");
      // path to edit article: "edit-article/0d5fb7b1-40ce-42ee-84f7-7a713d1bdec6"
    }
  };
  const isResponseEmpty = messageResponse === "";
  const showUndo = previousResponse || messageResponse === plainText;
  const showEdit = false; //removing this feature as you cannot edit a knowledge article without knowing which knowledge base it belongs too

  return /*#__PURE__*/React.createElement("div", {
    className: classes.knowledgeArticleContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.contentContainer
  }, /*#__PURE__*/React.createElement(Typography, {
    color: "textSecondary",
    variant: "subtitle1",
    gutterBottom: true
  }, t("Question:")), /*#__PURE__*/React.createElement(Typography, {
    color: "primary",
    gutterBottom: true
  }, article.question), /*#__PURE__*/React.createElement(Typography, {
    color: "textSecondary",
    variant: "subtitle1",
    gutterBottom: true
  }, t("Answer:")), /*#__PURE__*/React.createElement(RemoteContent, {
    content: article.answer,
    ref: articleEl,
    id: "kb-article"
  })), /*#__PURE__*/React.createElement("div", {
    className: classes.footerContainer
  }, copyToClipboard && /*#__PURE__*/React.createElement(CopyToClipboard, {
    text: plainText,
    onCopy: () => setCopied(true)
  }, /*#__PURE__*/React.createElement(PortalButton, {
    id: "copyAnswerButton",
    className: `${classes.copyAnswerButton} ${copied ? classes.copyAnswerButtonSuccess : ""}`
  }, copied ? t("Copied") : t("Copy Answer"))), !copyToClipboard && !showUndo && /*#__PURE__*/React.createElement(PortalButton, {
    id: "replaceAnswer",
    variant: "outlined",
    onClick: handleReplace,
    size: "small"
  }, isResponseEmpty ? "Copy" : "Copy & Replace"), !isResponseEmpty && !showUndo && /*#__PURE__*/React.createElement(PortalButton, {
    id: "appendAnswer",
    variant: "outlined",
    onClick: handleAppend,
    size: "small"
  }, "Copy & Append"), showUndo && /*#__PURE__*/React.createElement(PortalButton, {
    id: "undoCopy",
    variant: "outlined",
    onClick: handleUndo,
    size: "small"
  }, "Undo"), showEdit && /*#__PURE__*/React.createElement(PortalButton, {
    id: "edit",
    variant: "outlined",
    onClick: handleEdit,
    size: "small"
  }, "Edit")));
};
export default KnowledgeArticle;
KnowledgeArticle.propTypes = {
  article: PropTypes.object.isRequired,
  knowledgeBase: PropTypes.object.isRequired,
  copyToClipboard: PropTypes.bool
};